/**
 * Captura foto desde galería O cámara del celular.
 * Comprime localmente para no saturar la red ni MongoDB.
 *
 * Uso:
 *   <PhotoPicker fotos={fotos} setFotos={setFotos} max={5} />
 *
 *   - En iPhone/Android abre el sheet nativo: "Tomar foto" / "Foto biblioteca" / "Archivos"
 *   - Comprime a max 1024px (lado largo), JPEG 70%
 *   - Guarda como dataURL (base64) en el array fotos
 */
import { useRef } from "react";

async function fileToCompressed(file, maxDim = 1024, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxDim / width, maxDim / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataURL = canvas.toDataURL("image/jpeg", quality);
        resolve({ dataURL, width, height, kb: Math.round(dataURL.length / 1024) });
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PhotoPicker({ fotos, setFotos, max = 5, label, testIdPrefix = "foto" }) {
  const inputRef = useRef(null);

  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - fotos.length;
    const toProcess = files.slice(0, remaining);
    const nuevas = [];
    for (const f of toProcess) {
      try {
        const c = await fileToCompressed(f);
        nuevas.push({ id: Date.now() + Math.random(), dataURL: c.dataURL, kb: c.kb });
      } catch (err) {
        console.error("compress error", err);
      }
    }
    setFotos([...fotos, ...nuevas]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (id) => setFotos(fotos.filter((f) => f.id !== id));

  return (
    <div className="fotos-section">
      <div className="fotos-lbl">
        <i className="fa-solid fa-camera"/> {label || "FOTOS"} ({fotos.length}/{max})
      </div>
      <div className="fotos-grid">
        {fotos.map((f, i) => (
          <div key={f.id} className="foto-thumb" data-testid={`${testIdPrefix}-${i}`}>
            <img src={f.dataURL} alt={`foto ${i+1}`}/>
            <button
              type="button"
              className="foto-remove"
              onClick={() => remove(f.id)}
              aria-label="Quitar foto"
              data-testid={`${testIdPrefix}-remove-${i}`}
            >
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
        ))}
        {fotos.length < max && (
          <label className="foto-box add" data-testid={`${testIdPrefix}-add`}>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={onChange}
              style={{ display: "none" }}
            />
            <i className="fa-solid fa-camera"/>
            <span className="foto-add-lbl">Cámara · Galería</span>
          </label>
        )}
      </div>
    </div>
  );
}
